import './ShowInfo.scss';

import { EditOutlined } from '@ant-design/icons';
import { Button, Flex, Tooltip } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ShowInfoGridProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

interface ShowInfoItemProps {
  label?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Permite que el item ocupe más de una columna en la grilla. */
  colSpan?: 1 | 2;
  /** Permite que el item esté en línea con el label */
  inline?: boolean;
}

interface ModalFormActionsProps {
  onCancel: () => void;
  saveDisabled?: boolean;
  saveTooltip?: ReactNode;
}

// Metadata: grilla reutilizable para bloques label/valor en vistas de sólo lectura.
export const ShowInfoGrid = (props: ShowInfoGridProps) => {
  const { children, className, style } = props;

  return (
    <div style={style} className={`ShowInfoGrid ${className || ''}`}>
      {children}
    </div>
  );
};

// Metadata: encapsula label, acción opcional y valor para evitar HTML repetido en pantallas show.
export const ShowInfoItem = (props: ShowInfoItemProps) => {
  const { label, children, action, className, colSpan, inline } = props;
  const spanClass = colSpan === 2 ? 'ShowInfoItem--full' : '';
  const inlineClass = inline ? 'ShowInfoItem--inline' : '';

  return (
    <div className={`ShowInfoItem ${spanClass} ${inlineClass} ${className || ''}`}>
      <div className="ShowInfoItemHeader">
        {label && (
          <b>
            {label}
            {inline ? ': ' : null}
          </b>
        )}
        {action}
      </div>
      <div className="ShowInfoItemValue">{children ?? '-'}</div>
    </div>
  );
};

// Metadata: variante semántica para campos editables con ícono de edición junto al label.
export const EditableInfoItem = (props: ShowInfoItemProps) => {
  const { action, ...rest } = props;

  // Si ya se pasa una acción personalizada se usa; si no, se muestra el ícono de edición.
  return (
    <ShowInfoItem {...rest} action={action ?? <EditOutlined className="EditableInfoItemIcon" />} />
  );
};

// Metadata: acciones estándar para formularios dentro de modales de edición.
export const ModalFormActions = (props: ModalFormActionsProps) => {
  const { t } = useTranslation();
  const { onCancel, saveDisabled, saveTooltip } = props;

  return (
    <Flex className="ModalFormActions" justify="end" gap={8}>
      <Button onClick={onCancel}>{t('Cancelar')}</Button>
      <Tooltip title={saveTooltip}>
        <Button htmlType="submit" type="primary" disabled={saveDisabled}>
          {t('Guardar')}
        </Button>
      </Tooltip>
    </Flex>
  );
};
